/**
 * Portal 3 / Framerly thumbnail carousel — slide model and zoom helpers.
 *
 * This module is specific to `slider-th-framerly.tsx`. It does not share
 * types or helpers with the general product carousel (`product-slides.ts`).
 *
 * CustomizationMethod contract
 * ----------------------------
 * Redis `Framerly_SetThumbImages` fills `th1`–`th10` when
 * `CustomizationMethod >= 2`. This carousel has always rendered those slots
 * only when `CustomizationMethod === 2` (none=0 / old=1 never used this
 * strip). Visibility and the rendered list are the same descriptor array, so
 * method 0/1/3+ cannot show an empty Swiper: they produce zero descriptors.
 *
 * Exclusion rule (slots th1–th10, in order)
 * -----------------------------------------
 * Skip a slot when any of the following hold. The first remaining occurrence
 * of a normalized URL wins; later duplicates are dropped.
 *  1. `disabled === true` (undefined/null/false are treated as enabled).
 *  2. Value is not a string, or is blank / whitespace-only after trim.
 *  3. Invalid for CDN concatenation (`https://{CDN}{url}`): not a single
 *     root-relative path. Rejects `https://…`, `//host/…`, `/` only, and
 *     empty path-before-query. This prevents CDN-root requests.
 *  4. Duplicate of an earlier kept URL (trim + lowercase compare).
 */

export const FRAMERLY_THUMB_CUSTOMIZATION_METHOD = 2;

export const FRAMERLY_THUMB_SLOTS = ['th1', 'th2', 'th3', 'th4', 'th5', 'th6', 'th7', 'th8', 'th9', 'th10'] as const;

export type FramerlyThumbSlot = (typeof FRAMERLY_THUMB_SLOTS)[number];

export type FramerlyThumbSlotInput = {
    value?: string | null;
    disabled?: boolean | null;
};

export type FramerlyThumbsInput = {
    ready?: boolean;
    customizationMethod?: number | null;
    thumbs?: Partial<Record<FramerlyThumbSlot, FramerlyThumbSlotInput>>;
};

export type FramerlyThumbSlide = {
    key: FramerlyThumbSlot;
    url: string;
    ordinal: number;
    accessibleName: string;
    zoomAccessibleName: string;
    thumbAriaLabel: string;
};

export type FramerlyProductThumbFields = {
    CustomizationMethod?: number | null;
    th1?: string | null;
    th2?: string | null;
    th3?: string | null;
    th4?: string | null;
    th5?: string | null;
    th6?: string | null;
    th7?: string | null;
    th8?: string | null;
    th9?: string | null;
    th10?: string | null;
    th1Disabled?: boolean | null;
    th2Disabled?: boolean | null;
    th3Disabled?: boolean | null;
    th4Disabled?: boolean | null;
    th5Disabled?: boolean | null;
    th6Disabled?: boolean | null;
    th7Disabled?: boolean | null;
    th8Disabled?: boolean | null;
    th9Disabled?: boolean | null;
    th10Disabled?: boolean | null;
};

export type FramerlyZoomSwiperLike = {
    destroyed?: boolean;
    params?: {loop?: boolean};
    slideToLoop?: (index: number) => void;
    slideTo?: (index: number) => void;
};

/** Swiper min-width breakpoints matching getFramerlySlidesPerView. Default slidesPerView is 3. */
export const FRAMERLY_THUMB_SWIPER_BREAKPOINTS = {
    400: {slidesPerView: 4},
    510: {slidesPerView: 5},
    580: {slidesPerView: 6},
};

export function framerlyThumbsFromProduct(prod: FramerlyProductThumbFields | null | undefined): Partial<Record<FramerlyThumbSlot, FramerlyThumbSlotInput>> {
    if (!prod) {
        return {};
    }
    return {
        th1: {value: prod.th1, disabled: prod.th1Disabled},
        th2: {value: prod.th2, disabled: prod.th2Disabled},
        th3: {value: prod.th3, disabled: prod.th3Disabled},
        th4: {value: prod.th4, disabled: prod.th4Disabled},
        th5: {value: prod.th5, disabled: prod.th5Disabled},
        th6: {value: prod.th6, disabled: prod.th6Disabled},
        th7: {value: prod.th7, disabled: prod.th7Disabled},
        th8: {value: prod.th8, disabled: prod.th8Disabled},
        th9: {value: prod.th9, disabled: prod.th9Disabled},
        th10: {value: prod.th10, disabled: prod.th10Disabled},
    };
}

/**
 * Normalize a thumb field into a root-relative CDN path, or null if it must
 * not become a slide (blank, disabled already handled by caller, or invalid).
 */
export function normalizeFramerlyThumbUrl(value: unknown): string | null {
    if (typeof value !== 'string') {
        return null;
    }
    const trimmed = value.trim();
    if (!trimmed) {
        return null;
    }
    if (!trimmed.startsWith('/') || trimmed.startsWith('//')) {
        return null;
    }
    const pathOnly = trimmed.split(/[?#]/, 1)[0];
    if (!pathOnly || pathOnly === '/') {
        return null;
    }
    return trimmed;
}

function duplicateKey(url: string): string {
    return url.toLowerCase();
}

export function buildFramerlyThumbSlides(input: FramerlyThumbsInput): FramerlyThumbSlide[] {
    if (input.ready === false) {
        return [];
    }
    if (Number(input.customizationMethod) !== FRAMERLY_THUMB_CUSTOMIZATION_METHOD) {
        return [];
    }

    const seen = new Set<string>();
    const slides: FramerlyThumbSlide[] = [];
    const thumbs = input.thumbs ?? {};

    for (const key of FRAMERLY_THUMB_SLOTS) {
        const slot = thumbs[key];
        if (!slot || slot.disabled === true) {
            continue;
        }
        const url = normalizeFramerlyThumbUrl(slot.value);
        if (!url) {
            continue;
        }
        const dup = duplicateKey(url);
        if (seen.has(dup)) {
            continue;
        }
        seen.add(dup);
        const ordinal = slides.length + 1;
        slides.push({
            key,
            url,
            ordinal,
            accessibleName: `Real Life Image #${ordinal}`,
            zoomAccessibleName: `Real Image #${ordinal}`,
            thumbAriaLabel: `Open larger image of this real life image #${ordinal}`,
        });
    }

    return slides;
}

export function getFramerlySlidesPerView(width: number): number {
    if (!Number.isFinite(width)) {
        return 6;
    }
    if (width < 400) {
        return 3;
    }
    if (width < 510) {
        return 4;
    }
    if (width < 580) {
        return 5;
    }
    return 6;
}

export function shouldLoopFramerlyThumbs(slideCount: number, slidesPerView: number): boolean {
    return slideCount > slidesPerView;
}

export function shouldShowFramerlyThumbNavigation(slideCount: number, slidesPerView: number): boolean {
    return slideCount > slidesPerView;
}

export function shouldEnableFramerlyThumbKeyboard(slideCount: number, slidesPerView: number): boolean {
    return shouldLoopFramerlyThumbs(slideCount, slidesPerView);
}

export function shouldLoopFramerlyZoom(slideCount: number): boolean {
    return slideCount > 1;
}

export function findFramerlySlideIndexByKey(slides: ReadonlyArray<Pick<FramerlyThumbSlide, 'key'>>, key: string | null | undefined): number {
    if (typeof key !== 'string' || !key.trim()) {
        return -1;
    }
    return slides.findIndex(slide => slide.key === key);
}

export function framerlyThumbSrc(cdnDomain: string | null | undefined, url: string): string {
    return `https://${cdnDomain ?? ''}${url}`;
}

export function navigateFramerlyZoomSwiper(swiper: FramerlyZoomSwiperLike | null | undefined, index: number): boolean {
    if (!swiper || swiper.destroyed || !Number.isFinite(index) || index < 0) {
        return false;
    }
    try {
        if (swiper.params?.loop && typeof swiper.slideToLoop === 'function') {
            swiper.slideToLoop(index);
            return true;
        }
        if (typeof swiper.slideTo === 'function') {
            swiper.slideTo(index);
            return true;
        }
    } catch {
        return false;
    }
    return false;
}

/**
 * Jump to the opened slide once. Delayed 250ms/1250ms slideToLoop retries are
 * not scheduled — they raced unmount and called destroyed Swiper instances.
 */
export function scheduleFramerlyZoomNavigation(options: {swiper: FramerlyZoomSwiperLike | null | undefined; index: number; setTimeoutFn?: (handler: () => void, ms: number) => ReturnType<typeof setTimeout>}): Array<ReturnType<typeof setTimeout>> {
    void options.setTimeoutFn;
    navigateFramerlyZoomSwiper(options.swiper, options.index);
    return [];
}

export function clearFramerlyScheduledTimeouts(ids: Array<ReturnType<typeof setTimeout> | undefined | null>, clearTimeoutFn: (id: ReturnType<typeof setTimeout>) => void = id => clearTimeout(id as Parameters<typeof clearTimeout>[0])): void {
    for (const id of ids) {
        if (id != null) {
            clearTimeoutFn(id);
        }
    }
}

export type FramerlyCarouselSnapshot = {
    ready: boolean;
    customizationMethod?: number | null;
    thumbs?: Partial<Record<FramerlyThumbSlot, FramerlyThumbSlotInput>>;
    selectedKey?: string | null;
};

export function resolveFramerlyCarouselView(snapshot: FramerlyCarouselSnapshot): {
    slides: FramerlyThumbSlide[];
    visible: boolean;
    selectedIndex: number;
} {
    const slides = buildFramerlyThumbSlides({
        ready: snapshot.ready,
        customizationMethod: snapshot.customizationMethod,
        thumbs: snapshot.thumbs,
    });
    return {
        slides,
        visible: snapshot.ready && slides.length > 0,
        selectedIndex: findFramerlySlideIndexByKey(slides, snapshot.selectedKey),
    };
}
